import { ActionBar } from "@/components/environments/ActionBar";
import { EnvironmentDetailsHeader } from "@/components/environments/EnvironmentDetailsHeader";
import { EnvironmentOnboarding } from "@/components/environments/EnvironmentOnboarding";
import { EnvironmentPorts } from "@/components/environments/EnvironmentPorts";
import { EnvironmentServices } from "@/components/environments/EnvironmentServices";
import { EnvironmentStartSqeuence } from "@/components/environments/EnvironmentStartSequence";
import { EnvironmentTasks } from "@/components/environments/EnvironmentTasks";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { useListServices, useListTasks } from "@/queries/automations-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { useMemo, type FC, type PropsWithChildren } from "react";

export const EnvirontmentDetails: FC<{ environmentId: string }> = ({ environmentId }) => {
    return (
        <div className="mb-16 flex size-full flex-col gap-4 p-2">
            <EnvironmentDetailsHeader />
            <EnvironmentStartSqeuence environmentId={environmentId} />
            <WhenReady environmentId={environmentId}>
                <EnvironmentOnboarding environmentId={environmentId} />
                <EnvironmentPorts environmentId={environmentId} />
                <ShowAutomations environmentId={environmentId}>
                    <div className="flex items-center justify-between pb-2 pt-4">
                        <Heading2 className="select-none">Automations</Heading2>
                    </div>
                    <div className="flex flex-col gap-6">
                        <EnvironmentServices environmentId={environmentId} />
                        <EnvironmentTasks environmentId={environmentId} />
                    </div>
                </ShowAutomations>
                {!window.isWebview && <ActionBar environmentId={environmentId} />}
            </WhenReady>
        </div>
    );
};

const WhenReady: FC<PropsWithChildren & { environmentId: string }> = ({ children, environmentId }) => {
    const { data: environment, isPending } = useEnvironment(environmentId);
    const ready = !isPending && !!environment;

    if (!ready) {
        return null;
    }

    return children;
};

const ShowAutomations: FC<PropsWithChildren & { environmentId: string }> = ({ children, environmentId }) => {
    const { data: environment, isPending } = useEnvironment(environmentId);
    const { data: services } = useListServices(environmentId);
    const { data: tasks } = useListTasks(environmentId);

    const hide = useMemo(() => {
        // Once we have services we always want to show the section
        if (services && services.length > 0) {
            return false;
        }
        // Once we have tasks we always want to show the section
        if (tasks && tasks.length > 0) {
            return false;
        }

        // Until we have the environment we don't know if we should show the empty state, so default to not showing it
        // in order to avoid flickering
        if (isPending) {
            return true;
        }

        // If the environment is not created from a project then we show the onboarding section rather than
        // the empty state for services
        if (!environment?.metadata?.projectId) {
            return true;
        }

        return false;
    }, [services, tasks, isPending, environment?.metadata?.projectId]);

    if (hide) {
        return null;
    }

    return children;
};
